import React from 'react';
import styled from 'styled-components';
import Grid from '@material-ui/core/Grid';
import {AccentColorOptions, useBreakpointDown, useIsTwinmotionTheme} from 'epic-ue-theme';
import {
    GridContainer,
    CenteredGrid,
    VerticalSpacer,
    Typography,
    HiddenSSR,
    SubnavSectionConfigProps,
    SubnavSectionName
} from '../../elements';
import {OutlinedCard, OutlinedCardProps} from '../cards/outlined/OutlinedCard';
import {AnimateOnViewWrap} from '../../wraps/animation';

const Styled = styled.div`
    .description .paragraph {
        color: ${(p) => p.theme.palette.text.secondary};
    }
`;

export interface SectionIntroWithCardsProps {
    accentColor?: AccentColorOptions;
    cards?: OutlinedCardProps[];
    description?: string;
    heading?: string;
    largeCards?: boolean;
    componentMapperIndex?: number;
    manualSectionName?: string;
    sectionConfig?: SubnavSectionConfigProps;
    centerCards?: boolean;
}

export const SectionIntroWithCards = (props: SectionIntroWithCardsProps): JSX.Element => {
    const {
        accentColor,
        cards = [],
        description,
        heading = '',
        largeCards = false,
        componentMapperIndex,
        sectionConfig = {} as SubnavSectionConfigProps,
        manualSectionName,
        centerCards = false
    } = props;

    const isMd = useBreakpointDown('md');
    const isSm = useBreakpointDown('sm');
    const direction = isSm ? 'column' : 'row';
    const alignItems = isSm ? 'center' : 'stretch';
    const justifyCards = centerCards ? 'center' : isMd ? 'flex-start' : 'flex-end';
    const wrapCards = isSm ? 'wrap' : 'nowrap';

    const noVerticalPadding = {paddingTop: 0, paddingBottom: 0};
    const noVerticalMargin = {marginTop: 0, marginBottom: 0};
    const isTwinmotion = useIsTwinmotionTheme();
    let outlinedCardProps: OutlinedCardProps = {
        carbonAluminiumCards: isTwinmotion
    };

    if (largeCards) {
        outlinedCardProps = {
            ...outlinedCardProps,
            fillParentSize: true,
            topVerticalSpacing: 8,
            paragraphCaption: false,
            titleVariant: 'h2',
            titleAs: 'h3',
            titleBig: false
        };
    }

    return (
        <Styled className="section-intro-with-cards">
            <GridContainer>
                {componentMapperIndex === 0 && (
                    <Grid container>
                        <Grid item xs={12}>
                            <SubnavSectionName
                                {...sectionConfig}
                                sectionName={manualSectionName || sectionConfig.sectionName}
                            />
                        </Grid>
                    </Grid>
                )}
                <CenteredGrid
                    direction={direction}
                    justifyContent={'flex-start'}
                    alignItems={'stretch'}
                    style={noVerticalMargin}>
                    <Grid item sm={12} md={8} lg={largeCards ? 4 : 6} style={noVerticalPadding}>
                        <CenteredGrid style={noVerticalMargin}>
                            <Grid item xs={12} lg={largeCards ? 10 : 8} style={noVerticalPadding}>
                                <AnimateOnViewWrap translateY={30}>
                                    {heading && (
                                        <Typography variant="h2" big={true}>
                                            {heading}
                                        </Typography>
                                    )}
                                    {description && (
                                        <div className="description">
                                            <VerticalSpacer
                                                variant="custom"
                                                spacing={3}
                                                downSmSpacing={2}
                                            />
                                            <Typography variant="p">{description}</Typography>
                                        </div>
                                    )}
                                </AnimateOnViewWrap>
                                <HiddenSSR lgUp>
                                    <VerticalSpacer
                                        variant="custom"
                                        spacing={3}
                                        downSmSpacing={5}
                                    />
                                </HiddenSSR>
                            </Grid>
                        </CenteredGrid>
                    </Grid>

                    {cards.length ? (
                        <Grid
                            item
                            sm={12}
                            md={12}
                            lg={largeCards ? 8 : 6}
                            className="section-intro-cards"
                            style={noVerticalPadding}>
                            <CenteredGrid
                                justifyContent={justifyCards}
                                alignItems={alignItems}
                                wrap={wrapCards}
                                style={noVerticalMargin}>
                                {cards.map((cardProps, index) => {
                                    return (
                                        <Grid
                                            item
                                            xs={12}
                                            md={'auto'}
                                            key={`resources-card-outer-${index}`}
                                            style={noVerticalPadding}>
                                            <AnimateOnViewWrap
                                                translateY={largeCards ? 100 : 75}
                                                delay={0.3 * index}>
                                                <OutlinedCard
                                                    {...cardProps}
                                                    groupedHeight={true}
                                                    accentColor={accentColor}
                                                    topSpacing={true}
                                                    largeCards={largeCards}
                                                    {...outlinedCardProps}
                                                />
                                            </AnimateOnViewWrap>
                                            {index === 0 && (
                                                <VerticalSpacer
                                                    variant="custom"
                                                    spacing={0}
                                                    downSmSpacing={3}
                                                />
                                            )}
                                        </Grid>
                                    );
                                })}
                            </CenteredGrid>
                        </Grid>
                    ) : (
                        ''
                    )}
                </CenteredGrid>
            </GridContainer>
        </Styled>
    );
};
